/**
 * ─── SYSTEM-WIDE SETTINGS — the key vocabulary, and the first key's values. [C1h] ───
 *
 * A settings store is `dashboard_layouts`' opposite number, and its own header says why it
 * could not be reused: that collection is keyed on `(access_role, org_unit_type)` and is a
 * per-(role, type) DEFAULT. This is ONE value for the whole deployment.
 *
 * ⚠️ ONE DOCUMENT PER KEY, NEVER ONE SETTINGS DOCUMENT. The whole point is that adding the
 * second key is a WRITE and not a migration — a single document would make every new key a
 * schema change under `strict: 'throw'`, and a partial write to it a lost neighbour.
 */

/**
 * Every key the store recognises. A row whose key is not here is ignored on read rather
 * than failing the request — a key retired in a later release must not make the settings
 * screen unopenable on a database that still holds it.
 */
export const SETTING_KEYS = ['ui.mode', 'arrangement.nodalAuthor'] as const;

export type SettingKey = (typeof SETTING_KEYS)[number];

/**
 * THE DASHBOARD'S LOOK. `standard` is the monochrome palette this product has always had;
 * `colourful` is [C1h] §2's alternate set.
 *
 * ⚠️ DARK MODE IS UNCHANGED IN BOTH, AND THAT IS A PROPERTY OF THE CASCADE RATHER THAN A
 * PROMISE. `tokensCss.ts` emits `:root.dark` LAST, at equal specificity to
 * `:root.ui-colourful`, so dark wins whenever it applies. See that file's block order.
 *
 * ⚠️ AND IT GOVERNS THE ADMIN PANEL ONLY. `pwa/` was deliberately given its own palette
 * independent of the dashboard ([P5c.0]: "two products visually"), so this setting reaches
 * `web/` and nothing else. There is no officer-surface reader and none may be added without
 * answering what a colourful Duty Mitra would even mean.
 *
 * ⚠️ THE VALUES ARE ALSO THE KEYS OF `uiPalettes` in `@pams/client-core/theme`, which
 * declares them structurally rather than importing this file — that directory is loaded by
 * both apps' `vite.config.ts` through Node's own TypeScript loader, which resolves no
 * package export maps. `web/src/theme/uiMode.test.ts` pins the two against each other by
 * set equality, which is this repository's standing answer to two expressions of one thing.
 */
export const UI_MODES = ['standard', 'colourful'] as const;

export type UiMode = (typeof UI_MODES)[number];

/**
 * What a deployment gets before anybody has thrown the switch, and what an unreadable or
 * unrecognised stored value falls back to.
 *
 * ─── ⚠️ [amended C3a §9b] IT IS `colourful` NOW, AND THE ARGUMENT SURVIVES WITH THE VALUE ───
 *
 * [C1h] set this to `standard` on the reasoning that "a settings row that cannot be read must
 * not repaint the whole product; `standard` is what every screenshot, every contrast figure and
 * every operator's memory already agrees on." That reasoning is UNCHANGED and is why this is a
 * one-line edit rather than a redesign: after §9b the value everybody agrees on is `colourful`,
 * so the fallback still lands on what a reader expects to see.
 *
 * ⚠️ AND IT IS THE ONLY EXECUTABLE PLACE THE DEFAULT LIVES ON THIS SIDE. `readUiMode`,
 * `readCachedUiMode`, `getAuthConfig` and `ThemeProvider` all reach it through `asUiMode` or
 * directly, so they follow with no edit. The ONE thing that does NOT follow is the pre-paint
 * boot script, which cannot import this file — see `tokensCss.ts`'s `uiModeStandard`, and read
 * that comment before touching either.
 *
 * ⚠️ ITS ASYMMETRY WITH `DEFAULT_NODALS_MAY_AUTHOR_ARRANGEMENTS` IS UNCHANGED IN FORM, AND
 * FLIPPING THIS ONE IS NOT A LICENCE TO FLIP THAT ONE. A UI mode falls to what everybody
 * already knows; a CAPABILITY falls CLOSED. Same mechanism, opposite argument, and the reason
 * they are stated together is so neither is copied by mistake.
 *
 * ⚠️ NO MIGRATION SHIPS WITH THIS, AND THAT IS A DECISION. `updateUiMode` writes nothing when
 * the value is unchanged and the settings screen's Save is `disabled={!dirty}`, so a stored
 * `'standard'` row can only exist where somebody chose `colourful` and then switched BACK — a
 * deliberate preference. Deleting it would overrule the one person who expressed one, and
 * nothing about the stored shape changes, so there is nothing for a migration to reshape.
 */
export const DEFAULT_UI_MODE: UiMode = 'colourful';

/** Narrows an arbitrary stored string, so a retired or corrupt value cannot reach a palette lookup. */
export const asUiMode = (value: string | null | undefined): UiMode =>
  (UI_MODES as readonly string[]).includes(value ?? '') ? (value as UiMode) : DEFAULT_UI_MODE;

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [UX1 §8] WHETHER A NODAL ADMIN MAY RAISE AN ARRANGEMENT AT ALL — KEY #2, AND THE PROOF
 *  OF THIS STORE'S OWN PREMISE.
 *
 *  The header above says the whole point of one document per key is that adding the second is
 *  a WRITE and not a migration. It is: no model change, no index, no seeder, no deploy step.
 *  With zero rows the read answers the default and every screen renders accordingly.
 *
 *  ⚠️ IT DEFAULTS TO `false`, AND THAT IS A LIVE CAPABILITY CHANGE RATHER THAN A NEW TOGGLE.
 *  `mayAuthorArrangement` and `assertMayRaiseArrangement` refuse only a UNIT today, so a
 *  DIVISION nodal CAN raise an order and has been able to since [C1c §7]. This default takes
 *  that away until a super admin turns it back on. That is the client's instruction and it is
 *  recorded in the CHANGELOG as a BEHAVIOUR CHANGE, not as a feature.
 *
 *  ⚠️ AND THE FALLBACK DIRECTION IS THE OPPOSITE OF `asUiMode`'s, FOR THE OPPOSITE REASON.
 *  A UI mode that cannot be read must not repaint the product, so it falls to the value
 *  everybody already knows. A CAPABILITY that cannot be read must not be GRANTED, so it falls
 *  CLOSED. Same mechanism, opposite argument, both stated so neither is copied by mistake.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const DEFAULT_NODALS_MAY_AUTHOR_ARRANGEMENTS = false;

/**
 * Narrows the stored STRING — `settings.value` is one column for every key — and fails closed
 * on anything that is not exactly `'true'`.
 */
export const asNodalAuthorSetting = (value: string | null | undefined): boolean => value === 'true';
